import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { UserButton } from "@clerk/react";
import QuoteCarousel from "@/components/QuoteCarousel";
import { motion, useReducedMotion } from "framer-motion";
import { getMyDebts, listPools, getMe } from "@workspace/api-client-react";
import {
  Upload, Download, Clock, CheckCircle, XCircle,
  Loader2, AlertCircle, ShieldCheck, AlertTriangle, Flag,
  ChevronRight, ChevronLeft, FileUp, Info, Lock, FileText, RefreshCw, type LucideIcon,
} from "lucide-react";
import { LogoMark } from "@/components/Logo";
import LanguagePill from "@/components/LanguagePill";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import DebtorProfileControl from "@/components/debt-profiles/DebtorProfileControl";

const STATUS_CONFIG: Record<string, { color: string; icon: LucideIcon; label: string }> = {
  pending: { color: "bg-gray-100 text-gray-700", icon: Clock, label: "Under Review" },
  queued: { color: "bg-blue-100 text-blue-700", icon: Clock, label: "In Queue" },
  processing: { color: "bg-yellow-100 text-yellow-700", icon: Loader2, label: "Processing" },
  paid: { color: "bg-green-100 text-green-700", icon: CheckCircle, label: "Cleared!" },
  rejected: { color: "bg-red-100 text-red-700", icon: XCircle, label: "Rejected" },
};

const VERIFICATION_CONFIG: Record<string, { color: string; icon: LucideIcon; label: string }> = {
  verified: { color: "bg-green-100 text-green-700 border border-green-200", icon: ShieldCheck, label: "Verified" },
  manual_review: { color: "bg-amber-100 text-amber-700 border border-amber-200", icon: AlertTriangle, label: "Under Review" },
  flagged: { color: "bg-red-100 text-red-700 border border-red-200", icon: Flag, label: "Flagged" },
  unverified: { color: "bg-gray-100 text-gray-600 border border-gray-200", icon: Clock, label: "Verifying…" },
};

const NON_TERMINAL_DEBT_STATUSES = new Set(["pending", "queued", "processing"]);

// ---------------------------------------------------------------------------
// Debt status timeline
// ---------------------------------------------------------------------------

type TimelineStepState = "done" | "active" | "upcoming" | "failed";

interface TimelineStep {
  label: string;
  state: TimelineStepState;
  timestamp?: string | null;
}

function fmtDate(iso: string | null | undefined): string | undefined {
  if (!iso) return undefined;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

interface DebtTimestamps {
  createdAt?: string | null;
  verifiedAt?: string | null;
  queuedAt?: string | null;
  processingAt?: string | null;
  paidAt?: string | null;
}

function buildTimeline(
  status: string,
  verificationStatus: string | undefined | null,
  ts: DebtTimestamps = {},
): TimelineStep[] {
  const vs = verificationStatus ?? "unverified";

  const isVerified = vs === "verified" || ["queued", "processing", "paid"].includes(status);

  if (status === "rejected") {
    return [
      { label: "Submitted", state: "done", timestamp: fmtDate(ts.createdAt) },
      { label: "Verified", state: isVerified ? "done" : "failed", timestamp: fmtDate(ts.verifiedAt) },
      { label: "Queued", state: "failed" },
      { label: "Processing", state: "upcoming" },
      { label: "Cleared", state: "upcoming" },
    ];
  }

  if (vs === "flagged") {
    return [
      { label: "Submitted", state: "done", timestamp: fmtDate(ts.createdAt) },
      { label: "Verified", state: "failed", timestamp: fmtDate(ts.verifiedAt) },
      { label: "Queued", state: "upcoming" },
      { label: "Processing", state: "upcoming" },
      { label: "Cleared", state: "upcoming" },
    ];
  }

  const steps: TimelineStep[] = [
    { label: "Submitted", state: "done", timestamp: fmtDate(ts.createdAt) },
    {
      label: "Verified",
      state:
        isVerified
          ? "done"
          : vs === "manual_review"
          ? "active"
          : "active",
      timestamp: isVerified ? fmtDate(ts.verifiedAt) : undefined,
    },
    {
      label: "Queued",
      state:
        ["queued", "processing", "paid"].includes(status)
          ? status === "queued"
            ? "active"
            : "done"
          : "upcoming",
      timestamp: ["queued", "processing", "paid"].includes(status) ? fmtDate(ts.queuedAt) : undefined,
    },
    {
      label: "Processing",
      state:
        ["processing", "paid"].includes(status)
          ? status === "processing"
            ? "active"
            : "done"
          : "upcoming",
      timestamp: ["processing", "paid"].includes(status) ? fmtDate(ts.processingAt) : undefined,
    },
    {
      label: "Cleared",
      state: status === "paid" ? "done" : "upcoming",
      timestamp: status === "paid" ? fmtDate(ts.paidAt) : undefined,
    },
  ];

  return steps;
}

function DebtStatusTimeline({
  status,
  verificationStatus,
  timestamps,
}: {
  status: string;
  verificationStatus?: string | null;
  timestamps?: DebtTimestamps;
}) {
  const steps = buildTimeline(status, verificationStatus, timestamps);

  return (
    <div className="flex items-center gap-0 w-full mt-1" aria-label="Application progress">
      {steps.map((step, idx) => {
        const isLast = idx === steps.length - 1;
        return (
          <div key={step.label} className="flex items-center flex-1 min-w-0">
            {/* Node */}
            <div className="flex flex-col items-center shrink-0">
              <div
                className={[
                  "h-5 w-5 rounded-full flex items-center justify-center border-2 transition-colors",
                  step.state === "done"
                    ? "bg-green-500 border-green-500 text-white"
                    : step.state === "active"
                    ? "bg-primary border-primary text-white"
                    : step.state === "failed"
                    ? "bg-red-500 border-red-500 text-white"
                    : "bg-background border-muted-foreground/30 text-muted-foreground",
                ].join(" ")}
              >
                {step.state === "done" && (
                  <svg className="h-2.5 w-2.5" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
                {step.state === "active" && (
                  <div className="h-1.5 w-1.5 rounded-full bg-white" />
                )}
                {step.state === "failed" && (
                  <svg className="h-2.5 w-2.5" viewBox="0 0 12 12" fill="none">
                    <path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                )}
              </div>
              <span className="flex flex-col items-center gap-0">
                <span
                  className={[
                    "text-[10px] leading-tight mt-0.5 text-center whitespace-nowrap",
                    step.state === "done"
                      ? "text-green-600 font-medium"
                      : step.state === "active"
                      ? "text-primary font-semibold"
                      : step.state === "failed"
                      ? "text-red-500 font-medium"
                      : "text-muted-foreground/60",
                  ].join(" ")}
                >
                  {step.label}
                </span>
                {step.timestamp && (
                  <span className="text-[9px] leading-tight text-muted-foreground/50 whitespace-nowrap">
                    {step.timestamp}
                  </span>
                )}
              </span>
            </div>
            {/* Connector line */}
            {!isLast && (
              <div
                className={[
                  "flex-1 h-0.5 mx-0.5 mb-3.5 transition-colors",
                  step.state === "done" ? "bg-green-400" : "bg-muted-foreground/20",
                ].join(" ")}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

const DEBT_TYPES = [
  "Medical", "Credit Card", "Student Loan", "Auto Loan", "Mortgage",
  "Personal Loan", "Utility", "Rent/Housing", "Tax", "Other",
];

const EMPTY_FORM = {
  creditor: "",
  debtType: "",
  balance: "",
  poolId: "",
  creditorAddress: "",
  accountNumber: "",
  accountHolderName: "",
};

type MeWithIdentity = {
  identityCollectedAt?: string | null;
  terminatedAt?: string | null;
  terminationReason?: string | null;
};

export default function DebtorDashboard() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();
  const reduce = useReducedMotion();

  const { data: me } = useQuery({ queryKey: ["me"], queryFn: getMe, staleTime: 30_000 });
  const meExt = me as (typeof me & MeWithIdentity) | undefined;

  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [fcraConsent, setFcraConsent] = useState(false);
  const [verifyingDebtId, setVerifyingDebtId] = useState<number | null>(null);
  const [verificationResult, setVerificationResult] = useState<{
    verificationStatus: string;
    verificationScore: number | null;
    verificationNotes: string | null;
  } | null>(null);
  const [verifyingTimedOut, setVerifyingTimedOut] = useState(false);
  const [expandedNotes, setExpandedNotes] = useState<Set<number>>(new Set());

  function toggleNotes(debtId: number) {
    setExpandedNotes((prev) => {
      const next = new Set(prev);
      if (next.has(debtId)) {
        next.delete(debtId);
      } else {
        next.add(debtId);
      }
      return next;
    });
  }

  // Poll verification status every 3 seconds after upload
  useEffect(() => {
    if (!verifyingDebtId) return;

    const TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
    const startedAt = Date.now();
    let stopped = false;

    async function poll() {
      if (stopped) return;
      if (Date.now() - startedAt >= TIMEOUT_MS) {
        stopped = true;
        setVerifyingTimedOut(true);
        return;
      }
      try {
        const resp = await fetch(`/api/debts/${verifyingDebtId}/status`);
        if (!resp.ok) return;
        const data = await resp.json() as {
          isVerifying: boolean;
          verificationStatus: string;
          verificationScore: number | null;
          verificationNotes: string | null;
        };
        if (!data.isVerifying) {
          stopped = true;
          setVerificationResult({
            verificationStatus: data.verificationStatus,
            verificationScore: data.verificationScore,
            verificationNotes: data.verificationNotes,
          });
          setSubmitted(true);
          void qc.invalidateQueries({ queryKey: ["myDebts"] });
        }
      } catch {
        // silently retry
      }
    }

    void poll();
    const intervalId = setInterval(() => { void poll(); }, 3000);
    return () => {
      stopped = true;
      clearInterval(intervalId);
    };
  }, [verifyingDebtId, qc]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("unsubscribed") === "1") {
      toast({
        title: "Unsubscribed",
        description: "You have been unsubscribed from verification result emails. You can re-enable them in Notification Preferences below.",
      });
      const url = new URL(window.location.href);
      url.searchParams.delete("unsubscribed");
      window.history.replaceState({}, "", url.toString());
    }
  }, [toast]);

  const [payoffDebtId, setPayoffDebtId] = useState<number | null>(null);
  const [payoffFile, setPayoffFile] = useState<File | null>(null);
  const [uploadingPayoff, setUploadingPayoff] = useState(false);
  const [payoffUploaded, setPayoffUploaded] = useState(false);
  const payoffInputRef = useRef<HTMLInputElement>(null);

  const [tinDialogOpen, setTinDialogOpen] = useState(false);
  const [tinValue, setTinValue] = useState("");
  const [tinConsentChecked, setTinConsentChecked] = useState(false);
  const [tinSubmitting, setTinSubmitting] = useState(false);

  // Poll every 5s while any debt is still verifying OR in a non-terminal debt status.
  const { data: debts, isLoading, isError: debtsError, error: debtsErr } = useQuery({
    queryKey: ["myDebts"],
    queryFn: getMyDebts,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      const hasUnverified = data.some((d) => d.verificationStatus === "unverified");
      const hasActiveDebt = data.some((d) => NON_TERMINAL_DEBT_STATUSES.has(d.status));
      return hasUnverified || hasActiveDebt ? 5000 : false;
    },
  });

  // Track previous verification statuses so we can detect transitions and show toasts.
  // Must be declared after `debts` to avoid a TDZ ReferenceError.
  const prevVerificationStatuses = useRef<Record<number, string>>({});

  // Track previous debt statuses so we can fire toasts when the status advances.
  const prevDebtStatuses = useRef<Record<number, string>>({});

  useEffect(() => {
    if (!debts) return;

    const prev = prevVerificationStatuses.current;
    const changed: Array<{ id: number; creditor: string; status: string }> = [];

    for (const debt of debts) {
      const vs = (debt as { verificationStatus?: string }).verificationStatus ?? "unverified";
      const prevVs = prev[debt.id];

      // Only fire a toast if the debt previously had "unverified" status and now has a
      // terminal status — and it is not the debt currently being handled by the wizard
      // (the wizard shows its own confirmation UI for that case).
      if (prevVs === "unverified" && vs !== "unverified" && debt.id !== verifyingDebtId) {
        changed.push({ id: debt.id, creditor: debt.creditor, status: vs });
      }

      prev[debt.id] = vs;
    }

    for (const item of changed) {
      const isVerified = item.status === "verified";
      const isManual = item.status === "manual_review";
      toast({
        title: isVerified
          ? "Document Verified!"
          : isManual
          ? "Document Under Review"
          : "Document Flagged",
        description: isVerified
          ? `Your document for ${item.creditor} passed verification and has been placed in a relief pool.`
          : isManual
          ? `Your document for ${item.creditor} requires manual review. Our team will follow up within 1–2 business days.`
          : `There was an issue with your document for ${item.creditor}. Our team will review it and may reach out.`,
        variant: isVerified || isManual ? "default" : "destructive",
      });
    }
  }, [debts, verifyingDebtId, toast]);

  // Fire toasts when a debt's overall status advances (queued, processing, paid, rejected).
  useEffect(() => {
    if (!debts) return;

    const prev = prevDebtStatuses.current;
    const changed: Array<{ id: number; creditor: string; prevStatus: string; status: string }> = [];

    for (const debt of debts) {
      const prevStatus = prev[debt.id];
      if (prevStatus !== undefined && prevStatus !== debt.status) {
        changed.push({ id: debt.id, creditor: debt.creditor, prevStatus, status: debt.status });
      }
      prev[debt.id] = debt.status;
    }

    for (const item of changed) {
      if (item.status === "queued") {
        toast({
          title: "Added to Relief Queue",
          description: `Your ${item.creditor} debt has been verified and placed in a relief queue. We'll process it soon.`,
        });
      } else if (item.status === "processing") {
        toast({
          title: "Payment In Progress",
          description: `DebtBridge is sending payment to ${item.creditor} on your behalf. Upload a payoff confirmation when you receive it.`,
        });
      } else if (item.status === "paid") {
        toast({
          title: "Debt Cleared!",
          description: `Your ${item.creditor} debt has been paid off. Download your forgiveness letter below.`,
        });
      } else if (item.status === "rejected") {
        toast({
          title: "Application Rejected",
          description: `Your ${item.creditor} debt application was not approved. Contact support if you have questions.`,
          variant: "destructive",
        });
      }
    }
  }, [debts, toast]);

  const { data: tinStatus, refetch: refetchTinStatus } = useQuery({
    queryKey: ["tinStatus"],
    queryFn: async () => {
      const resp = await fetch("/api/debts/tin-status");
      if (!resp.ok) return null;
      return resp.json() as Promise<{ hasTin: boolean; tinProvidedAt: string | null }>;
    },
    enabled: Boolean(debts && debts.length > 0),
  });

  interface TaxDocument {
    id: number;
    type: string;
    taxYear: number;
    creditor: string | null;
    cancelledAmount: number | null;
    sentAt: string | null;
    createdAt: string;
    debtId: number | null;
    downloadUrl: string | null;
  }

  const hasPaidDebt = Boolean(debts && debts.some((d) => d.status === "paid"));

  const { data: taxDocumentsRaw } = useQuery({
    queryKey: ["taxDocuments1099c"],
    queryFn: async () => {
      const resp = await fetch("/api/debtor/letters?type=form_1099c");
      if (!resp.ok) return [] as TaxDocument[];
      return resp.json() as Promise<TaxDocument[]>;
    },
    enabled: hasPaidDebt,
  });

  // When debts are loaded but none are paid, the query never runs — derive
  // the empty array immediately so the UI shows an empty state, not a skeleton.
  const taxDocuments: TaxDocument[] | undefined =
    !hasPaidDebt && debts !== undefined ? [] : taxDocumentsRaw;

  async function submitTin() {
    const digits = tinValue.replace(/\D/g, "");
    if (digits.length !== 9) {
      toast({ title: "Invalid TIN", description: "Please enter exactly 9 digits.", variant: "destructive" });
      return;
    }
    if (!tinConsentChecked) {
      toast({ title: "Consent required", description: "Please check the authorization checkbox before submitting.", variant: "destructive" });
      return;
    }
    setTinSubmitting(true);
    try {
      const resp = await fetch("/api/debts/tin", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tin: digits }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({})) as { error?: string };
        throw new Error(err.error ?? "Failed to save TIN");
      }
      toast({ title: "TIN saved", description: "Your Tax ID Number has been securely stored." });
      setTinDialogOpen(false);
      setTinValue("");
      setTinConsentChecked(false);
      void refetchTinStatus();
    } catch (err) {
      toast({ title: "Error", description: err instanceof Error ? err.message : "Failed to save TIN", variant: "destructive" });
    } finally {
      setTinSubmitting(false);
    }
  }

  function formatTinDisplay(raw: string): string {
    const digits = raw.replace(/\D/g, "").slice(0, 9);
    if (digits.length <= 3) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  }

  const { data: pools } = useQuery({
    queryKey: ["pools"],
    queryFn: () => listPools(),
  });

  const activePools = pools?.items ?? [];

  const { data: notifPrefs, refetch: refetchNotifPrefs } = useQuery({
    queryKey: ["notifPrefs"],
    queryFn: async () => {
      const resp = await fetch("/api/users/me/notifications");
      if (!resp.ok) throw new Error("Failed to fetch notification preferences");
      return resp.json() as Promise<{
        emailVerificationResults: boolean;
        emailQueued: boolean;
        emailPaymentSent: boolean;
        emailDebtCleared: boolean;
        emailRejected: boolean;
      }>;
    },
  });

  const notifMutation = useMutation({
    mutationFn: async (patch: Partial<{ emailVerificationResults: boolean; emailQueued: boolean; emailPaymentSent: boolean; emailDebtCleared: boolean; emailRejected: boolean }>) => {
      const resp = await fetch("/api/users/me/notifications", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (!resp.ok) throw new Error("Failed to update notification preferences");
      return resp.json() as Promise<{ emailVerificationResults: boolean; emailQueued: boolean; emailPaymentSent: boolean; emailDebtCleared: boolean; emailRejected: boolean }>;
    },
    onSuccess: () => {
      void refetchNotifPrefs();
      toast({ title: "Preferences saved", description: "Your notification settings have been updated." });
    },
    onError: () => {
      toast({ title: "Error", description: "Could not save preferences. Please try again.", variant: "destructive" });
    },
  });

  function downloadLetter(debtId: number) {
    window.open(`/api/debts/${debtId}/letter`, "_blank");
  }

  function openPayoffDialog(debtId: number) {
    setPayoffDebtId(debtId);
    setPayoffFile(null);
    setPayoffUploaded(false);
  }

  function closePayoffDialog() {
    if (uploadingPayoff) return;
    setPayoffDebtId(null);
    setPayoffFile(null);
    setPayoffUploaded(false);
  }

  async function submitPayoffConfirmation() {
    if (!payoffFile || !payoffDebtId) return;
    setUploadingPayoff(true);
    try {
      const fd = new FormData();
      fd.append("file", payoffFile);
      const resp = await fetch(`/api/debts/${payoffDebtId}/payoff-confirmation`, {
        method: "POST",
        body: fd,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({})) as { error?: string };
        throw new Error(err.error ?? "Upload failed");
      }
      setPayoffUploaded(true);
      void qc.invalidateQueries({ queryKey: ["myDebts"] });
    } catch (err) {
      toast({
        title: "Upload failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setUploadingPayoff(false);
    }
  }

  function openWizard() {
    setForm(EMPTY_FORM);
    setSelectedFile(null);
    setFilePreviewUrl(null);
    setDragActive(false);
    setWizardStep(1);
    setSubmitted(false);
    setFcraConsent(false);
    setVerifyingDebtId(null);
    setVerificationResult(null);
    setVerifyingTimedOut(false);
    setWizardOpen(true);
  }

  function applyFile(file: File) {
    setSelectedFile(file);
    if (file.type.startsWith("image/")) {
      const url = URL.createObjectURL(file);
      setFilePreviewUrl(url);
    } else {
      setFilePreviewUrl(null);
    }
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) applyFile(file);
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) applyFile(file);
  }

  async function handleSubmit() {
    if (!selectedFile) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("consentGiven", String(fcraConsent));
      formData.append("creditor", form.creditor);
      formData.append("creditorAddress", form.creditorAddress);
      formData.append("accountNumber", form.accountNumber);
      formData.append("accountHolderName", form.accountHolderName);
      formData.append("balance", form.balance);
      if (form.debtType) formData.append("debtType", form.debtType);
      if (form.poolId) formData.append("poolId", form.poolId);

      const resp = await fetch("/api/debts/upload", {
        method: "POST",
        body: formData,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({})) as { error?: string };
        throw new Error(err.error ?? "Upload failed");
      }
      const data = await resp.json() as { debtId: number };
      void qc.invalidateQueries({ queryKey: ["myDebts"] });
      setVerifyingDebtId(data.debtId);
    } catch (err) {
      toast({
        title: "Submission failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  }

  const canProceedStep1 = form.creditor.trim().length > 0 && form.debtType.length > 0 && parseFloat(form.balance) > 0;
  const canProceedStep2 = form.accountHolderName.trim().length > 0;
  const canProceedStep3 = selectedFile !== null;

  if (meExt?.terminatedAt) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 py-12">
        <div className="w-full max-w-md text-center space-y-5">
          <div className="p-4 rounded-full bg-red-100 w-16 h-16 flex items-center justify-center mx-auto">
            <XCircle className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Account Closed</h1>
          <p className="text-muted-foreground">
            Your account has been permanently closed following a compliance review. All associated debt submissions have been rejected.
          </p>
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-900 text-left">
            <p>If you believe this decision was made in error, please contact us at{" "}
              <a href="mailto:support@debtbridge.org" className="underline font-medium">support@debtbridge.org</a>{" "}
              with your account details.
            </p>
          </div>
          <UserButton />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: "linear-gradient(180deg, #f5f8ff 0%, #f8f9fc 100%)" }}>
      {/* Wildlife/nature — misty forest, ghost texture at 4% */}
      <img
        src="https://images.unsplash.com/photo-1448375240586-882707db888b?w=1600&q=80&fit=crop&auto=format"
        alt="" aria-hidden loading="lazy"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.13, filter: "grayscale(0.1)", pointerEvents: "none" }}
      />
      <nav className="sticky top-0 z-50 border-b bg-gradient-to-r from-blue-950 to-slate-800 border-blue-900">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LogoMark size={28} />
            <span className="font-semibold text-white">{t("debtorDashboard.portalLabel")}</span>
            <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-sky-400 text-sky-950 tracking-wide">
              {t("debtorDashboard.roleBadge")}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <LanguagePill light />
            <UserButton />
          </div>
        </div>
      </nav>

      <motion.div
        className="max-w-4xl mx-auto px-4 py-8 space-y-8"
        initial={reduce ? false : { opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      >
        {/* API error banner — shown when critical debtor endpoints return an error */}
        {debtsError && (() => {
          const isRoleNotConfirmed =
            debtsErr !== null &&
            typeof debtsErr === "object" &&
            (debtsErr as Record<string, unknown>)["status"] === 403 &&
            typeof (debtsErr as Record<string, unknown>)["data"] === "object" &&
            (debtsErr as Record<string, unknown>)["data"] !== null &&
            ((debtsErr as Record<string, unknown>)["data"] as Record<string, unknown>)["code"] === "ROLE_NOT_CONFIRMED";
          return (
            <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900">
              <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
              <div className="text-sm flex-1">
                {isRoleNotConfirmed ? (
                  <>
                    <p className="font-semibold">Your account role needs to be confirmed</p>
                    <p className="mt-0.5 text-amber-800">
                      Your session needs a quick role confirmation.{" "}
                      <a href="/select-role" className="underline font-medium hover:no-underline">
                        Confirm now →
                      </a>
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-semibold">Unable to load your debt applications</p>
                    <p className="mt-0.5 text-amber-800">
                      Please refresh the page. If the problem persists, sign out and sign back in.
                    </p>
                  </>
                )}
              </div>
              <button
                onClick={() => window.location.reload()}
                className="shrink-0 flex items-center gap-1 text-xs font-medium underline hover:no-underline"
              >
                <RefreshCw className="h-3 w-3" /> Refresh
              </button>
            </div>
          );
        })()}

        {/* Submit Debt Prompt */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-primary" />
              {t("debtorDashboard.submitDebtTitle")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t("debtorDashboard.submitDebtDesc")}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center text-xs text-muted-foreground">
              {[
                t("debtorDashboard.step1"),
                t("debtorDashboard.step2"),
                t("debtorDashboard.step3"),
                t("debtorDashboard.step4"),
              ].map((step, i) => (
                <div key={i} className="flex flex-col items-center gap-1">
                  <div className="h-7 w-7 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center text-sm">{i + 1}</div>
                  <span className="leading-tight">{step}</span>
                </div>
              ))}
            </div>
            <Button className="w-full" onClick={openWizard}>
              <Upload className="h-4 w-4 mr-2" />
              {t("debtorDashboard.startSubmission")}
            </Button>
          </CardContent>
        </Card>

        {/* TIN / SSN Collection Banner */}
        {debts && debts.length > 0 && (
          tinStatus?.hasTin ? (
            <div className="rounded-lg border border-green-200 bg-green-50 p-4 flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 shrink-0" />
              <div className="flex-1 text-sm text-green-900">
                <span className="font-semibold">{t("debtorDashboard.taxIdOnFile")}</span> — {t("debtorDashboard.taxIdOnFileDesc")}
              </div>
              <Button size="sm" variant="outline" className="text-xs" onClick={() => { setTinValue(""); setTinConsentChecked(false); setTinDialogOpen(true); }}>
                {t("debtorDashboard.update")}
              </Button>
            </div>
          ) : (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1 space-y-1">
                <p className="text-sm font-semibold text-amber-900">{t("debtorDashboard.taxIdRequired")}</p>
                <p className="text-sm text-amber-800">
                  {t("debtorDashboard.taxIdRequiredDesc")}
                </p>
              </div>
              <Button size="sm" className="shrink-0" onClick={() => { setTinValue(""); setTinConsentChecked(false); setTinDialogOpen(true); }}>
                <Lock className="h-3.5 w-3.5 mr-1.5" />
                {t("debtorDashboard.addSsnItin")}
              </Button>
            </div>
          )
        )}

        {/* TIN Entry Dialog */}
        <Dialog open={tinDialogOpen} onOpenChange={(open) => { if (!tinSubmitting) { setTinDialogOpen(open); if (!open) setTinConsentChecked(false); } }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-primary" />
                Enter Your Tax ID Number
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800 space-y-2">
                <p className="font-semibold">Why we collect this &amp; how it is protected</p>
                <p>
                  The IRS requires your SSN or ITIN on <strong>Form 1099-C (Cancellation of Debt)</strong> for any forgiven debt of $600 or more. This is a legal obligation — without it, we cannot complete IRS filing on your behalf.
                </p>
                <ul className="list-disc pl-4 space-y-1">
                  <li><strong>Stored:</strong> Encrypted at rest using AES-256-GCM. The raw number is never logged or returned by the API.</li>
                  <li><strong>Access:</strong> Only authorized DebtBridge staff for IRS filing purposes.</li>
                  <li><strong>Shared:</strong> With the IRS only, as required by law. Never sold or shared with third parties.</li>
                  <li><strong>Retained:</strong> 7 years per IRS nonprofit recordkeeping rules (IRC § 6001).</li>
                </ul>
              </div>
              <div className="space-y-2">
                <Label htmlFor="tin-input">SSN or ITIN (9 digits)</Label>
                <Input
                  id="tin-input"
                  type="text"
                  inputMode="numeric"
                  placeholder="XXX-XX-XXXX"
                  value={tinValue}
                  onChange={(e) => setTinValue(formatTinDisplay(e.target.value))}
                  maxLength={11}
                  autoComplete="off"
                  className="font-mono tracking-widest"
                />
                <p className="text-xs text-muted-foreground">
                  Format: 123-45-6789. Dashes are optional.
                </p>
              </div>
              <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-border p-3 hover:bg-muted/40 transition-colors">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 shrink-0 accent-primary cursor-pointer"
                  checked={tinConsentChecked}
                  onChange={(e) => setTinConsentChecked(e.target.checked)}
                  disabled={tinSubmitting}
                />
                <span className="text-xs text-muted-foreground leading-relaxed">
                  I authorize DebtBridge Foundation to collect and securely store my SSN or ITIN for IRS Form 1099-C reporting purposes, as described above.
                </span>
              </label>
              <div className="flex gap-2 pt-1">
                <Button
                  variant="ghost"
                  className="flex-1"
                  onClick={() => { setTinDialogOpen(false); setTinConsentChecked(false); }}
                  disabled={tinSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1"
                  onClick={() => void submitTin()}
                  disabled={tinSubmitting || tinValue.replace(/\D/g, "").length !== 9 || !tinConsentChecked}
                >
                  {tinSubmitting ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</>
                  ) : (
                    <><Lock className="h-4 w-4 mr-2" />Save Securely</>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* My Debts */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle>{t("debtorDashboard.myApplications")}</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : !debts || debts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>{t("debtorDashboard.noApplicationsYet")}</p>
                <p className="text-sm mt-1">{t("debtorDashboard.submitFirstDebt")}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {debts.map((debt) => {
                  const cfg = STATUS_CONFIG[debt.status] ?? STATUS_CONFIG.pending;
                  const StatusIcon = cfg.icon;
                  const vs = debt.verificationStatus;
                  const vc = vs ? (VERIFICATION_CONFIG[vs] ?? VERIFICATION_CONFIG.unverified) : null;
                  const VIcon = vc?.icon;
                  const score = debt.verificationScore != null ? Math.round(debt.verificationScore) : null;
                  const notes = debt.verificationNotes ?? null;
                  const notesExpanded = expandedNotes.has(debt.id);
                  return (
                    <div key={debt.id} className="p-4 rounded-xl border space-y-3">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="font-semibold">{debt.creditor}</div>
                          <div className="text-sm text-muted-foreground">
                            Balance: <span className="font-medium text-foreground">${Number(debt.balance).toLocaleString()}</span>
                          </div>
                          {debt.poolName && (
                            <div className="text-xs text-muted-foreground mt-0.5">Pool: {debt.poolName}</div>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-1.5 shrink-0">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
                            <StatusIcon className="h-3 w-3" />
                            {cfg.label}
                          </span>
                          {vc && VIcon && (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${vc.color}`}>
                              <VIcon className="h-3 w-3" />
                              {vc.label}
                              {score !== null && (
                                <span className="font-semibold ml-0.5">· {score}%</span>
                              )}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Status timeline */}
                      <div className="pt-1 pb-0.5">
                        <DebtStatusTimeline
                          status={debt.status}
                          verificationStatus={(debt as { verificationStatus?: string }).verificationStatus}
                          timestamps={{
                            createdAt: debt.createdAt,
                            verifiedAt: debt.verifiedAt,
                            queuedAt: debt.queuedAt,
                            processingAt: debt.processingAt,
                            paidAt: debt.paidAt,
                          }}
                        />
                      </div>

                      <DebtorProfileControl
                        debtId={debt.id}
                        initialStory={(debt as typeof debt & { publicProfileStory?: string | null }).publicProfileStory}
                        profileStatus={(debt as typeof debt & { publicProfileStatus?: string | null }).publicProfileStatus}
                      />

                      {notes && (
                        <div>
                          <button
                            type="button"
                            onClick={() => toggleNotes(debt.id)}
                            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                          >
                            <Info className="h-3 w-3" />
                            {notesExpanded ? "Hide verification notes" : "Show verification notes"}
                          </button>
                          {notesExpanded && (
                            <div className="mt-1.5 rounded-lg border bg-muted/40 px-3 py-2 text-xs text-muted-foreground leading-relaxed">
                              {notes}
                            </div>
                          )}
                        </div>
                      )}

                      {debt.status === "queued" && debt.queuePosition && (
                        <div className="text-sm text-muted-foreground">
                          Queue position: #{debt.queuePosition}
                        </div>
                      )}

                      {debt.status === "queued" && tinStatus && !tinStatus.hasTin && (
                        <div className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-1.5">
                          <p className="text-sm font-semibold text-red-900 flex items-center gap-1.5">
                            <Lock className="h-4 w-4 shrink-0" />
                            Payout on hold — SSN/TIN required
                          </p>
                          <p className="text-xs text-red-800">
                            Your payout cannot proceed until you submit your Social Security Number or ITIN. This is required by the IRS for Form 1099-C (Cancellation of Debt) filing.
                          </p>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-900 border-red-400 hover:bg-red-100 mt-1"
                            onClick={() => { setTinValue(""); setTinConsentChecked(false); setTinDialogOpen(true); }}
                          >
                            <Lock className="h-3.5 w-3.5 mr-1.5" />
                            Submit SSN / ITIN now
                          </Button>
                        </div>
                      )}

                      {debt.status === "processing" && (
                        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm">
                          <p className="font-medium text-yellow-900 flex items-center gap-1.5">
                            <FileUp className="h-4 w-4" />
                            Payment sent — confirm receipt
                          </p>
                          <p className="text-yellow-700 text-xs mt-1">
                            DebtBridge has sent payment to your creditor. Once you receive a payoff confirmation letter from them, upload it here to complete the process.
                          </p>
                        </div>
                      )}

                      <div className="flex gap-2 flex-wrap">
                        {debt.status === "processing" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-yellow-800 border-yellow-400 hover:bg-yellow-50"
                            onClick={() => openPayoffDialog(debt.id)}
                          >
                            <FileUp className="h-3 w-3 mr-1" />
                            Upload Payoff Confirmation
                          </Button>
                        )}
                        {debt.status === "paid" && (
                          <Button size="sm" onClick={() => downloadLetter(debt.id)}>
                            <Download className="h-3 w-3 mr-1" />
                            Download Forgiveness Letter
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tax Documents */}
        {!taxDocuments ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <FileText className="h-4 w-4 text-primary" />
                Tax Documents
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[0, 1].map((i) => <Skeleton key={i} className="h-16 w-full rounded-lg" />)}
              </div>
            </CardContent>
          </Card>
        ) : taxDocuments.length === 0 ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <FileText className="h-4 w-4 text-primary" />
                Tax Documents
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm font-medium text-muted-foreground">No tax documents yet</p>
                <p className="text-xs text-muted-foreground mt-1">
                  IRS Form 1099-C documents will appear here when your debts are cancelled and reported.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <FileText className="h-4 w-4 text-primary" />
                Tax Documents
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Your IRS Form 1099-C (Cancellation of Debt) documents are listed below. Download links are valid for 7 days.
              </p>
              <div className="space-y-3">
                {taxDocuments.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between gap-4 p-3 rounded-lg border bg-muted/20"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">
                        Form 1099-C — Tax Year {doc.taxYear}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        {doc.creditor ?? "Unknown Creditor"}
                        {doc.cancelledAmount != null && (
                          <> &bull; Cancelled: <span className="font-medium text-foreground">${doc.cancelledAmount.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span></>
                        )}
                      </p>
                      {doc.sentAt && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Issued {new Date(doc.sentAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                        </p>
                      )}
                    </div>
                    {doc.downloadUrl ? (
                      <a
                        href={doc.downloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        download={`form-1099c-${doc.taxYear}-debt-${doc.debtId ?? doc.id}.pdf`}
                      >
                        <Button size="sm" variant="outline" className="shrink-0 gap-1.5">
                          <Download className="h-3.5 w-3.5" />
                          Download
                        </Button>
                      </a>
                    ) : (
                      <Button size="sm" variant="outline" className="shrink-0 gap-1.5" disabled>
                        <Download className="h-3.5 w-3.5" />
                        Unavailable
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* How It Works */}
        <Card className="bg-muted/30">
          <CardContent className="pt-5">
            <h3 className="font-semibold mb-3">How the Process Works</h3>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>1. <strong>Submit</strong> — Complete the 4-step form with your debt and creditor details</p>
              <p>2. <strong>Verify</strong> — Our AI reviews your document and checks for consistency</p>
              <p>3. <strong>Queue</strong> — Verified debts are automatically placed in a relief pool</p>
              <p>4. <strong>Cleared</strong> — When the pool is funded, your debt is paid and you receive a forgiveness letter</p>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Priority fee ($10) moves your debt to the front of the queue within your pool.
            </p>
          </CardContent>
        </Card>

        {/* Notification Preferences */}
        <Card id="notifications">
          <CardHeader>
            <CardTitle className="text-base">{t("debtorDashboard.notificationPrefs")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-primary cursor-pointer"
                checked={notifPrefs?.emailVerificationResults ?? true}
                disabled={notifMutation.isPending}
                onChange={(e) => notifMutation.mutate({ emailVerificationResults: e.target.checked })}
              />
              <div>
                <p className="text-sm font-medium group-hover:text-primary transition-colors">
                  Verification result emails
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Receive an email when your document finishes AI verification (verified, flagged, or under manual review).
                </p>
              </div>
            </label>
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-primary cursor-pointer"
                checked={notifPrefs?.emailQueued ?? true}
                disabled={notifMutation.isPending}
                onChange={(e) => notifMutation.mutate({ emailQueued: e.target.checked })}
              />
              <div>
                <p className="text-sm font-medium group-hover:text-primary transition-colors">
                  Queue placement emails
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Receive an email when your application is accepted and added to a relief pool queue.
                </p>
              </div>
            </label>
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-primary cursor-pointer"
                checked={notifPrefs?.emailPaymentSent ?? true}
                disabled={notifMutation.isPending}
                onChange={(e) => notifMutation.mutate({ emailPaymentSent: e.target.checked })}
              />
              <div>
                <p className="text-sm font-medium group-hover:text-primary transition-colors">
                  Payment sent emails
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Receive an email when DebtBridge sends a payment to your creditor on your behalf.
                </p>
              </div>
            </label>
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-primary cursor-pointer"
                checked={notifPrefs?.emailDebtCleared ?? true}
                disabled={notifMutation.isPending}
                onChange={(e) => notifMutation.mutate({ emailDebtCleared: e.target.checked })}
              />
              <div>
                <p className="text-sm font-medium group-hover:text-primary transition-colors">
                  Debt cleared emails
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Receive an email (with your forgiveness letter attached) when your debt has been fully cleared.
                </p>
              </div>
            </label>
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-primary cursor-pointer"
                checked={notifPrefs?.emailRejected ?? true}
                disabled={notifMutation.isPending}
                onChange={(e) => notifMutation.mutate({ emailRejected: e.target.checked })}
              />
              <div>
                <p className="text-sm font-medium group-hover:text-primary transition-colors">
                  Application rejection emails
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Receive an email if your debt relief application is not approved.
                </p>
              </div>
            </label>
            {notifMutation.isPending && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" /> Saving…
              </p>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* 4-Step Submission Wizard */}
      <Dialog open={wizardOpen} onOpenChange={(open) => { if (!open && !submitting) { setWizardOpen(false); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {submitted
                ? "Verification Complete"
                : verifyingDebtId && !verifyingTimedOut
                ? "Verifying Document…"
                : verifyingTimedOut
                ? "Verification Timed Out"
                : `Step ${wizardStep} of 4 — ${
                    wizardStep === 1 ? "Debt Information" :
                    wizardStep === 2 ? "Creditor Details" :
                    wizardStep === 3 ? "Upload Document" :
                    "Review & Consent"
                  }`}
            </DialogTitle>
          </DialogHeader>

          {submitted ? (
            <div className="space-y-4 py-2">
              {(() => {
                const vs = verificationResult?.verificationStatus ?? "verified";
                const vc = VERIFICATION_CONFIG[vs] ?? VERIFICATION_CONFIG.unverified;
                const VIcon = vc.icon;
                const isVerified = vs === "verified";
                const isManual = vs === "manual_review";
                const isFlagged = vs === "flagged";
                return (
                  <>
                    <div className="flex flex-col items-center gap-4 py-4">
                      <div className={`h-14 w-14 rounded-full flex items-center justify-center ${
                        isVerified ? "bg-green-100" : isManual ? "bg-amber-100" : "bg-red-100"
                      }`}>
                        <VIcon className={`h-8 w-8 ${
                          isVerified ? "text-green-600" : isManual ? "text-amber-600" : "text-red-600"
                        }`} />
                      </div>
                      <div className="text-center space-y-1">
                        <p className="font-semibold text-lg">
                          {isVerified ? "Document Verified!" : isManual ? "Under Manual Review" : "Document Flagged"}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {isVerified
                            ? "Your document passed AI verification and has been placed in a relief pool."
                            : isManual
                            ? "Our team will review your document manually. You'll hear back within 1–2 business days."
                            : "There was an issue with your document. Our team will review it and may reach out for more information."}
                        </p>
                      </div>
                    </div>
                    <div className="rounded-lg border bg-muted/30 p-4 space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-green-700">
                        <CheckCircle className="h-4 w-4" /> Document uploaded
                      </div>
                      <div className={`flex items-center gap-2 ${vc.color.includes("green") ? "text-green-700" : vc.color.includes("amber") ? "text-amber-700" : "text-red-700"}`}>
                        <VIcon className="h-4 w-4" /> {vc.label}
                        {verificationResult?.verificationScore != null && (
                          <span className="ml-auto text-xs text-muted-foreground">Score: {verificationResult.verificationScore}%</span>
                        )}
                      </div>
                      {isVerified && (
                        <div className="flex items-center gap-2 text-blue-700">
                          <ShieldCheck className="h-4 w-4" /> Added to relief pool
                        </div>
                      )}
                      {verificationResult?.verificationNotes && (
                        <p className="text-xs text-muted-foreground pt-1 border-t">{verificationResult.verificationNotes}</p>
                      )}
                    </div>
                    <Button className="w-full" onClick={() => setWizardOpen(false)}>
                      {t("debtorDashboard.viewMyDashboard")}
                    </Button>
                  </>
                );
              })()}
            </div>
          ) : verifyingDebtId && !verifyingTimedOut ? (
            <div className="space-y-4 py-2">
              <div className="flex flex-col items-center gap-6 py-6">
                <div className="relative h-16 w-16">
                  <div className="absolute inset-0 rounded-full bg-blue-100 animate-ping opacity-40" />
                  <div className="relative h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                    <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
                  </div>
                </div>
                <div className="text-center space-y-1">
                  <p className="font-semibold text-lg">Verifying your document…</p>
                  <p className="text-sm text-muted-foreground">
                    Our AI is reviewing your debt statement. This usually takes less than a minute.
                  </p>
                </div>
              </div>
              <div className="rounded-lg border bg-muted/30 p-4 space-y-2 text-sm">
                <div className="flex items-center gap-2 text-green-700">
                  <CheckCircle className="h-4 w-4" /> Document uploaded
                </div>
                <div className="flex items-center gap-2 text-blue-700">
                  <Loader2 className="h-4 w-4 animate-spin" /> AI verification in progress…
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="h-4 w-4" /> Pool placement pending verification result
                </div>
              </div>
              <Button variant="outline" className="w-full" onClick={() => setWizardOpen(false)}>
                Close and check back later
              </Button>
            </div>
          ) : verifyingTimedOut ? (
            <div className="space-y-4 py-2">
              <div className="flex flex-col items-center gap-4 py-4">
                <div className="h-14 w-14 rounded-full bg-amber-100 flex items-center justify-center">
                  <AlertTriangle className="h-8 w-8 text-amber-600" />
                </div>
                <div className="text-center">
                  <p className="font-semibold text-lg">Verification is taking longer than expected</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Your document was submitted successfully, but verification is still running in the background. Check your dashboard in a few minutes for the result.
                  </p>
                </div>
              </div>
              <div className="rounded-lg border bg-muted/30 p-4 space-y-2 text-sm">
                <div className="flex items-center gap-2 text-green-700">
                  <CheckCircle className="h-4 w-4" /> Document uploaded
                </div>
                <div className="flex items-center gap-2 text-amber-700">
                  <Clock className="h-4 w-4" /> Verification still running — check back soon
                </div>
              </div>
              <Button className="w-full" onClick={() => setWizardOpen(false)}>
                Close
              </Button>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Step progress */}
              <div className="flex gap-1.5">
                {[1, 2, 3, 4].map((s) => (
                  <div
                    key={s}
                    className={`flex-1 h-1 rounded-full transition-colors ${s <= wizardStep ? "bg-primary" : "bg-muted"}`}
                  />
                ))}
              </div>

              {wizardStep === 1 && (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="w-creditor">Creditor Name <span className="text-destructive">*</span></Label>
                    <Input
                      id="w-creditor"
                      placeholder="e.g. Northwell Health, Capital One, Chase"
                      value={form.creditor}
                      onChange={(e) => setForm((f) => ({ ...f, creditor: e.target.value }))}
                      maxLength={100}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="w-debttype">Debt Type <span className="text-destructive">*</span></Label>
                    <select
                      id="w-debttype"
                      className="w-full text-sm border rounded-md px-3 py-2 bg-background"
                      value={form.debtType}
                      onChange={(e) => setForm((f) => ({ ...f, debtType: e.target.value }))}
                    >
                      <option value="">Select debt type…</option>
                      {DEBT_TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="w-balance">Total Balance Owed ($) <span className="text-destructive">*</span></Label>
                    <Input
                      id="w-balance"
                      type="number"
                      placeholder="e.g. 5200"
                      min={1}
                      step={0.01}
                      value={form.balance}
                      onChange={(e) => setForm((f) => ({ ...f, balance: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="w-pool">Relief Pool (optional)</Label>
                    <select
                      id="w-pool"
                      className="w-full text-sm border rounded-md px-3 py-2 bg-background"
                      value={form.poolId}
                      onChange={(e) => setForm((f) => ({ ...f, poolId: e.target.value }))}
                    >
                      <option value="">Auto-select best pool</option>
                      {activePools.map((p) => (
                        <option key={p.id} value={String(p.id)}>
                          {p.icon ? `${p.icon} ` : ""}{p.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-muted-foreground">
                      If left blank, we'll match you by creditor name, or an admin will assign you.
                    </p>
                  </div>
                </div>
              )}

              {wizardStep === 2 && (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="w-holder">Account Holder Name <span className="text-red-500">*</span></Label>
                    <Input
                      id="w-holder"
                      placeholder="Legal name as it appears on the account"
                      value={form.accountHolderName}
                      onChange={(e) => setForm((f) => ({ ...f, accountHolderName: e.target.value }))}
                      maxLength={100}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="w-acct">Account Number</Label>
                    <Input
                      id="w-acct"
                      placeholder="e.g. ****-1234 or as shown on statement"
                      value={form.accountNumber}
                      onChange={(e) => setForm((f) => ({ ...f, accountNumber: e.target.value }))}
                      maxLength={50}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="w-addr">Creditor Mailing Address</Label>
                    <Input
                      id="w-addr"
                      placeholder="e.g. PO Box 12345, Austin TX 78701"
                      value={form.creditorAddress}
                      onChange={(e) => setForm((f) => ({ ...f, creditorAddress: e.target.value }))}
                      maxLength={200}
                    />
                  </div>
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-100">
                    <AlertCircle className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                    <p className="text-xs text-blue-700">
                      These details are used to verify your document with AI and contact your creditor for relief. Accuracy improves your verification score.
                    </p>
                  </div>
                </div>
              )}

              {wizardStep === 3 && (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Upload a PDF or image of your debt statement, collection notice, or billing invoice.
                  </p>
                  <div
                    className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                      dragActive
                        ? "border-primary bg-primary/10"
                        : selectedFile
                        ? "border-primary/50 bg-primary/5"
                        : "hover:border-primary/40 hover:bg-muted/30"
                    }`}
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                    onDragLeave={() => setDragActive(false)}
                    onDrop={handleDrop}
                  >
                    {selectedFile ? (
                      <div className="flex flex-col items-center gap-2">
                        {filePreviewUrl ? (
                          <img
                            src={filePreviewUrl}
                            alt="Document preview"
                            className="max-h-32 max-w-full rounded object-contain border"
                          />
                        ) : (
                          <CheckCircle className="h-8 w-8 text-primary" />
                        )}
                        <p className="text-sm font-medium truncate max-w-xs">{selectedFile.name}</p>
                        <p className="text-xs text-muted-foreground">Click or drag to replace</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <Upload className="h-8 w-8 text-muted-foreground" />
                        <p className="text-sm font-medium">{dragActive ? "Drop file here" : "Drag & drop or click to select"}</p>
                        <p className="text-xs text-muted-foreground">PDF or image · Max 10MB</p>
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/*"
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                </div>
              )}

              {wizardStep === 4 && (
                <div className="space-y-4">
                  <div className="rounded-lg border p-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Creditor</span>
                      <span className="font-medium">{form.creditor}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Debt Type</span>
                      <span className="font-medium">{form.debtType}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Balance</span>
                      <span className="font-medium">${parseFloat(form.balance || "0").toLocaleString()}</span>
                    </div>
                    {form.accountHolderName && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Account Holder</span>
                        <span className="font-medium">{form.accountHolderName}</span>
                      </div>
                    )}
                    {form.accountNumber && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Account #</span>
                        <span className="font-medium">{form.accountNumber}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Document</span>
                      <span className="font-medium text-primary truncate max-w-[180px]">{selectedFile?.name}</span>
                    </div>
                  </div>

                  <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4 space-y-3">
                    <p className="text-sm font-semibold text-amber-900">FCRA Consent & Disclosure</p>
                    <ul className="text-xs text-amber-800 leading-relaxed list-disc list-inside space-y-1">
                      <li>Process your document using AI/OCR to extract debt details</li>
                      <li>Obtain your consumer financial information under the <strong>Fair Credit Reporting Act (FCRA)</strong></li>
                      <li>Potentially purchase your debt from your creditor and act as a debt buyer</li>
                      <li>Authorize DebtBridge to contact your creditor and facilitate debt elimination on your behalf</li>
                    </ul>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 rounded border-amber-400 accent-amber-700 cursor-pointer"
                        checked={fcraConsent}
                        onChange={(e) => setFcraConsent(e.target.checked)}
                      />
                      <span className="text-xs text-amber-800 leading-snug">
                        I have read and agree to the above disclosures. I expressly consent to DebtBridge processing my consumer financial information under the Fair Credit Reporting Act (FCRA).
                      </span>
                    </label>
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex gap-3 pt-2">
                {wizardStep > 1 && (
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setWizardStep((s) => s - 1)}
                    disabled={submitting}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" /> Back
                  </Button>
                )}
                {wizardStep < 4 ? (
                  <Button
                    className="flex-1"
                    disabled={
                      (wizardStep === 1 && !canProceedStep1) ||
                      (wizardStep === 2 && !canProceedStep2) ||
                      (wizardStep === 3 && !canProceedStep3)
                    }
                    onClick={() => setWizardStep((s) => s + 1)}
                  >
                    Next <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                ) : (
                  <Button
                    className="flex-1"
                    disabled={submitting || !fcraConsent}
                    onClick={handleSubmit}
                  >
                    {submitting ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Verifying document…</>
                    ) : (
                      "Submit for Relief"
                    )}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payoff Confirmation Upload Dialog */}
      <Dialog open={payoffDebtId !== null} onOpenChange={(o) => { if (!o) closePayoffDialog(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Payoff Confirmation</DialogTitle>
          </DialogHeader>

          {payoffUploaded ? (
            <div className="space-y-4 py-2">
              <div className="flex flex-col items-center gap-4 py-4">
                <div className="h-14 w-14 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <div className="text-center">
                  <p className="font-semibold text-lg">Confirmation Received</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Our team has been notified. Your debt will be marked as cleared once verified.
                  </p>
                </div>
              </div>
              <Button className="w-full" onClick={closePayoffDialog}>Done</Button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Upload the payoff confirmation letter you received from your creditor. Accepted formats: PDF, PNG, JPG.
              </p>

              <div
                className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:bg-muted/20 transition-colors"
                onClick={() => payoffInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const file = e.dataTransfer.files?.[0];
                  if (file) setPayoffFile(file);
                }}
              >
                <input
                  ref={payoffInputRef}
                  type="file"
                  className="hidden"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) setPayoffFile(file);
                  }}
                />
                {payoffFile ? (
                  <div className="space-y-1">
                    <FileUp className="h-6 w-6 mx-auto text-primary" />
                    <p className="text-sm font-medium">{payoffFile.name}</p>
                    <p className="text-xs text-muted-foreground">{(payoffFile.size / 1024).toFixed(1)} KB</p>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPayoffFile(null);
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <Upload className="h-6 w-6 mx-auto text-muted-foreground" />
                    <p className="text-sm font-medium">Click or drag to upload</p>
                    <p className="text-xs text-muted-foreground">PDF, PNG, or JPG up to 10 MB</p>
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <Button variant="ghost" className="flex-1" onClick={closePayoffDialog} disabled={uploadingPayoff}>
                  Cancel
                </Button>
                <Button
                  className="flex-1"
                  disabled={!payoffFile || uploadingPayoff}
                  onClick={submitPayoffConfirmation}
                >
                  {uploadingPayoff ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading…</>
                  ) : (
                    <><FileUp className="h-4 w-4 mr-2" />Submit Confirmation</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <QuoteCarousel />
    </div>
  );
}
